import { defaultProductType } from '../../lib/nodeDefaults';
import {
  formatHourlyEquivalent,
  formatIncomingRate,
  formatInputUnit,
  isMassFlowInputUnit,
  quantityUnitFromInputUnit,
  QUANTITY_UNIT_PRESETS,
} from '../../lib/inputRateUnits';
import ProductVisualPicker from './ProductVisualPicker';
import type {
  GenerationProfile,
  ProductTypeConfig,
} from '../../types/nodeConfig';

interface Props {
  node: Record<string, unknown>;
  readOnly?: boolean;
  onChange: (patch: Record<string, unknown>) => void;
}

function productList(node: Record<string, unknown>): ProductTypeConfig[] {
  return (node.product_types as ProductTypeConfig[] | undefined) ?? [];
}

function profile(node: Record<string, unknown>): GenerationProfile {
  return (
    (node.generation_profile as GenerationProfile | undefined) ?? {
      type: 'constant',
      rate: Number(node.products_per_minute) || 1,
    }
  );
}

export default function InputNodeConfig({ node, readOnly, onChange }: Props) {
  const products = productList(node);
  const gen = profile(node);
  const totalPct = products.reduce((s, p) => s + (p.percentage || 0), 0);
  const ratePerMinute = Number(node.products_per_minute) || 0;
  const inputUnit = String(node.input_unit ?? 'kg/min');
  const quantityUnit = quantityUnitFromInputUnit(inputUnit);
  const customQuantityUnit = QUANTITY_UNIT_PRESETS.includes(
    quantityUnit as (typeof QUANTITY_UNIT_PRESETS)[number],
  )
    ? ''
    : quantityUnit;

  const setQuantityUnit = (nextQty: string) => {
    const qty = nextQty.trim() || 'kg';
    onChange({ input_unit: formatInputUnit(qty) });
  };

  const syncConstantProfileRate = (rate: number) => {
    const patch: Record<string, unknown> = { products_per_minute: rate };
    const current = profile(node);
    if (current.type === 'constant') {
      patch.generation_profile = { type: 'constant', rate };
    }
    onChange(patch);
  };

  const setProducts = (next: ProductTypeConfig[]) =>
    onChange({ product_types: next });

  const normalize = () => {
    const enabled = products.filter((p) => p.enabled !== false);
    const sum = enabled.reduce((s, p) => s + p.percentage, 0) || 1;
    setProducts(
      products.map((p) =>
        p.enabled === false
          ? p
          : { ...p, percentage: Math.round((p.percentage / sum) * 1000) / 10 },
      ),
    );
  };

  return (
    <section className="space-y-4 rounded-lg border border-border bg-surface/40 p-4">
      <h3 className="panel-section-title">Input configuration</h3>

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <label className="mb-1 block text-xs text-text-muted">
            Total incoming rate
            <span className="ml-1 font-normal text-text-subtle">(per simulation minute)</span>
          </label>
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="number"
              min={0}
              step={0.1}
              className="input-field w-28"
              disabled={readOnly}
              value={ratePerMinute}
              onChange={(e) =>
                syncConstantProfileRate(Number(e.target.value))
              }
            />
            <select
              className="input-field w-28"
              disabled={readOnly}
              value={
                customQuantityUnit
                  ? '__custom__'
                  : quantityUnit || 'kg'
              }
              onChange={(e) => {
                if (e.target.value !== '__custom__') {
                  setQuantityUnit(e.target.value);
                }
              }}
            >
              {QUANTITY_UNIT_PRESETS.map((unit) => (
                <option key={unit} value={unit}>
                  {unit}
                </option>
              ))}
              <option value="__custom__">Custom…</option>
            </select>
            <span className="text-sm text-text-muted">/ min</span>
          </div>
          {customQuantityUnit && (
            <input
              type="text"
              className="input-field mt-2 w-full max-w-xs"
              disabled={readOnly}
              placeholder="Custom quantity unit (e.g. crates)"
              value={customQuantityUnit}
              onChange={(e) => setQuantityUnit(e.target.value)}
            />
          )}
          <p className="mt-2 text-xs text-text-subtle">
            <span className="font-medium text-text">
              {formatIncomingRate(ratePerMinute, inputUnit)}
            </span>
            {ratePerMinute > 0 && (
              <span className="text-text-muted">
                {' '}
                · {formatHourlyEquivalent(ratePerMinute, inputUnit)} equivalent
              </span>
            )}
          </p>
          <p className="mt-1 text-xs text-text-subtle">
            The simulation always generates at this rate per minute. Product mix
            percentages split this total across product types.
            {isMassFlowInputUnit(inputUnit) ? (
              <span className="mt-1 block text-sky-200/90">
                Mass flow mode: set each product&apos;s weight (kg) below. The
                engine converts {formatIncomingRate(1, inputUnit).replace(/^1 /, '')}{' '}
                into discrete items using those weights. Tons/min are converted to
                kg automatically (1 ton = 1000 kg).
              </span>
            ) : (
              <span className="mt-1 block">
                Count flow mode: rate is items per minute (use kg as quantity
                unit above to switch to mass flow).
              </span>
            )}
          </p>
        </div>
        <div className="sm:col-span-2">
          <label className="mb-1 block text-xs text-text-muted">
            Product unit label
            <span className="ml-1 font-normal text-text-subtle">
              (optional description)
            </span>
          </label>
          <input
            type="text"
            className="input-field w-full"
            disabled={readOnly}
            value={String(node.product_unit_label ?? '')}
            onChange={(e) => onChange({ product_unit_label: e.target.value })}
            placeholder="e.g. 1 kg of shrimp"
          />
        </div>
      </div>

      <div>
        <div className="mb-2 flex items-center justify-between">
          <p className="text-xs font-medium text-text-muted">Product mix</p>
          <div className="flex items-center gap-2">
            <span
              className={`text-xs ${
                Math.abs(totalPct - 100) < 0.05
                  ? 'text-success-on-dark'
                  : 'text-warning-on-dark'
              }`}
            >
              Total: {totalPct.toFixed(1)}%
            </span>
            {!readOnly && (
              <>
                <button
                  type="button"
                  className="btn-ghost px-2 py-1 text-xs"
                  onClick={normalize}
                >
                  Normalize
                </button>
                <button
                  type="button"
                  className="btn-secondary px-2 py-1 text-xs"
                  onClick={() =>
                    setProducts([
                      ...products,
                      defaultProductType(`Product ${products.length + 1}`),
                    ])
                  }
                >
                  Add product
                </button>
              </>
            )}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="text-text-subtle">
              <tr>
                <th className="pb-2 pr-2">Name</th>
                <th className="pb-2 pr-2">%</th>
                <th className="pb-2 pr-2" title="Mass of one product token in kilograms">
                  Weight (kg)
                </th>
                <th className="pb-2 pr-2" title="Unit label for each product in the mix (not the time rate)">
                  Item unit
                </th>
                <th className="pb-2 pr-2">Look</th>
                <th className="pb-2 pr-2">On</th>
                <th className="pb-2" />
              </tr>
            </thead>
            <tbody>
              {products.map((p, index) => (
                <tr key={p.id} className="border-t border-border/60">
                  <td className="py-2 pr-2">
                    <input
                      className="input-field py-1"
                      disabled={readOnly}
                      value={p.name}
                      onChange={(e) => {
                        const next = [...products];
                        next[index] = { ...p, name: e.target.value };
                        setProducts(next);
                      }}
                    />
                  </td>
                  <td className="py-2 pr-2">
                    <input
                      type="number"
                      className="input-field w-16 py-1"
                      disabled={readOnly}
                      value={p.percentage}
                      onChange={(e) => {
                        const next = [...products];
                        next[index] = {
                          ...p,
                          percentage: Number(e.target.value),
                        };
                        setProducts(next);
                      }}
                    />
                  </td>
                  <td className="py-2 pr-2">
                    <input
                      type="number"
                      min={0.001}
                      step={0.1}
                      className="input-field w-16 py-1"
                      disabled={readOnly}
                      value={p.weight_kg ?? 1}
                      onChange={(e) => {
                        const next = [...products];
                        next[index] = {
                          ...p,
                          weight_kg: Number(e.target.value),
                        };
                        setProducts(next);
                      }}
                    />
                  </td>
                  <td className="py-2 pr-2">
                    <input
                      className="input-field py-1"
                      disabled={readOnly}
                      value={p.unit}
                      onChange={(e) => {
                        const next = [...products];
                        next[index] = { ...p, unit: e.target.value };
                        setProducts(next);
                      }}
                    />
                  </td>
                  <td className="py-2 pr-2">
                    <ProductVisualPicker
                      color={p.color}
                      shape={p.shape}
                      size={p.size}
                      readOnly={readOnly}
                      onChange={(visual) => {
                        const next = [...products];
                        next[index] = { ...p, ...visual };
                        setProducts(next);
                      }}
                    />
                  </td>
                  <td className="py-2 pr-2">
                    <input
                      type="checkbox"
                      disabled={readOnly}
                      checked={p.enabled !== false}
                      onChange={(e) => {
                        const next = [...products];
                        next[index] = { ...p, enabled: e.target.checked };
                        setProducts(next);
                      }}
                    />
                  </td>
                  <td className="py-2 text-right">
                    {!readOnly && (
                      <div className="flex justify-end gap-1">
                        <button
                          type="button"
                          className="btn-ghost px-1 text-xs"
                          title="Duplicate product"
                          onClick={() => {
                            const copy: ProductTypeConfig = {
                              ...p,
                              id: crypto.randomUUID(),
                              name: `${p.name} (copy)`,
                            };
                            const next = [...products];
                            next.splice(index + 1, 0, copy);
                            setProducts(next);
                          }}
                        >
                          Dup
                        </button>
                        <button
                          type="button"
                          className="btn-danger-ghost px-1"
                          onClick={() =>
                            setProducts(products.filter((_, i) => i !== index))
                          }
                        >
                          Remove
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="space-y-3">
        <p className="text-xs font-medium text-text-muted">Generation profile</p>
        <select
          className="input-field w-full max-w-xs"
          disabled={readOnly}
          value={gen.type}
          onChange={(e) => {
            const t = e.target.value;
            if (t === 'bell_curve') {
              onChange({
                generation_profile: {
                  type: 'bell_curve',
                  baseline_rate: 10,
                  peak_rate: 52,
                  peak_time_seconds: 2100,
                  spread_seconds: 1200,
                  start_time_seconds: 0,
                  end_time_seconds: 5400,
                },
              });
            } else if (t === 'step_schedule') {
              onChange({
                generation_profile: {
                  type: 'step_schedule',
                  steps: [
                    { start_time_seconds: 0, end_time_seconds: 600, rate: 10 },
                    {
                      start_time_seconds: 600,
                      end_time_seconds: 2400,
                      rate: 38,
                    },
                  ],
                },
              });
            } else {
              onChange({
                generation_profile: {
                  type: 'constant',
                  rate: Number(node.products_per_minute) || 38,
                },
              });
            }
          }}
        >
          <option value="constant">Constant rate</option>
          <option value="bell_curve">Bell curve / arrival wave</option>
          <option value="step_schedule">Step schedule</option>
        </select>

        {gen.type === 'constant' && (
          <p className="text-xs text-text-subtle">
            Uses {formatIncomingRate(ratePerMinute, inputUnit)} for the full
            simulation.
          </p>
        )}

        {gen.type === 'bell_curve' && (
          <>
            <p className="mb-2 text-xs text-text-subtle">
              Profile rates use the same unit as total incoming rate (
              {formatIncomingRate(1, inputUnit).replace(/^1 /, '')}).
            </p>
            <div className="grid gap-2 sm:grid-cols-2">
            {(
              [
                ['baseline_rate', 'Baseline rate'],
                ['peak_rate', 'Peak rate'],
                ['peak_time_seconds', 'Peak time (sec)'],
                ['spread_seconds', 'Spread (sec)'],
              ] as const
            ).map(([key, label]) => (
              <div key={key}>
                <label className="mb-1 block text-xs text-text-muted">{label}</label>
                <input
                  type="number"
                  className="input-field w-full"
                  disabled={readOnly}
                  value={gen[key]}
                  onChange={(e) =>
                    onChange({
                      generation_profile: {
                        ...gen,
                        [key]: Number(e.target.value),
                      },
                    })
                  }
                />
              </div>
            ))}
          </div>
          </>
        )}

        {gen.type === 'step_schedule' && (
          <>
            <p className="mb-2 text-xs text-text-subtle">
              Step rates use the same unit as total incoming rate (
              {formatIncomingRate(1, inputUnit).replace(/^1 /, '')}).
            </p>
            <div className="space-y-2">
            {gen.steps.map((step, index) => (
              <div key={index} className="grid grid-cols-3 gap-2">
                <input
                  type="number"
                  className="input-field py-1 text-xs"
                  disabled={readOnly}
                  value={step.start_time_seconds}
                  onChange={(e) => {
                    const steps = [...gen.steps];
                    steps[index] = {
                      ...step,
                      start_time_seconds: Number(e.target.value),
                    };
                    onChange({
                      generation_profile: { ...gen, steps },
                    });
                  }}
                  placeholder="Start sec"
                />
                <input
                  type="number"
                  className="input-field py-1 text-xs"
                  disabled={readOnly}
                  value={step.end_time_seconds}
                  onChange={(e) => {
                    const steps = [...gen.steps];
                    steps[index] = {
                      ...step,
                      end_time_seconds: Number(e.target.value),
                    };
                    onChange({
                      generation_profile: { ...gen, steps },
                    });
                  }}
                  placeholder="End sec"
                />
                <input
                  type="number"
                  className="input-field py-1 text-xs"
                  disabled={readOnly}
                  value={step.rate}
                  onChange={(e) => {
                    const steps = [...gen.steps];
                    steps[index] = { ...step, rate: Number(e.target.value) };
                    onChange({
                      generation_profile: { ...gen, steps },
                    });
                  }}
                  placeholder="Rate"
                />
              </div>
            ))}
            {!readOnly && (
              <button
                type="button"
                className="btn-ghost text-xs"
                onClick={() =>
                  onChange({
                    generation_profile: {
                      ...gen,
                      steps: [
                        ...gen.steps,
                        {
                          start_time_seconds: 0,
                          end_time_seconds: 600,
                          rate: 20,
                        },
                      ],
                    },
                  })
                }
              >
                Add step
              </button>
            )}
          </div>
          </>
        )}
      </div>
    </section>
  );
}
